<script setup lang="ts">
    import { computed, onMounted, reactive, ref, toRaw } from 'vue';
    import ConfirmationModal from '@/components/ConfirmationModal.vue';
    import axios from 'axios';
    import { useRoute } from 'vue-router';
import { formatAmount, formatDate } from '@/utils';
    
    const route = useRoute();
    const errorMessage = ref('');
    const loading = ref(false);
    const itemsList = ref([] as any[]);
    const selectedItem = ref(null as any);
    const confirmationModalRef = ref<InstanceType<typeof ConfirmationModal> | null>(null);
    const selectedItems = ref([] as any[]);
    const areAllItemsSelected = computed(() => {
        return itemsList.value.length == selectedItems.value.length;
    });
    const startupInfos = reactive({
        users: [] as any[]
    });
    const queryParams = reactive({
        pageNumber: 1,
        sortDir: 'DESC',
        sortColumn: 'created_at',
        sortOption: 'created_at_desc',
        borneInf: '',
        borneSup: '',
        user_id: '',
        search: '',
    });
    const pagination = reactive({
        total: 0,
        per_page: 10,
        last_page: 1
    });
    const SORT_OPTIONS = [
        { value: 'created_at_asc', label: 'Date (ASC)', sort_column: 'created_at', sort_direction: 'ASC' },
        { value: 'created_at_desc', label: 'Date (DESC)', sort_column: 'created_at', sort_direction: 'DESC' },
    ]

    onMounted(() => {
        const sortParam = route.query.sort as string;
        if (sortParam) { queryParams.sortOption = sortParam; onFiltersChanged(); }
        doListItems();
    });

    async function doListItems() {
        try {
            loading.value = true;
            errorMessage.value = '';
            const queryParamsRaw = toRaw(queryParams);
            const queryString = new URLSearchParams(Object.fromEntries(Object.entries(queryParamsRaw).map(([k,v]) => [k, String(v)]))).toString();
            const httpResponse = await axios.get(`/backoffice/market-commandes/list?${queryString}`);
            itemsList.value = httpResponse.data.data;
            pagination.total = httpResponse.data.total;
            pagination.per_page = httpResponse.data.per_page;
            pagination.last_page = httpResponse.data.last_page;
        } catch (error: any) {
            errorMessage.value = error.message;
        } finally {
            loading.value = false;
        }
    }

    function handleCheckAll(_event: any) {
        const checked = _event.target.checked;
        if (checked) {
            selectedItems.value = itemsList.value.map(i => i.id);
        } else {
            selectedItems.value = [];
        }
    }

    function onFiltersChanged() {
        const sortItem = SORT_OPTIONS.find(o => o.value == queryParams.sortOption);
        if (sortItem) {
            queryParams.sortColumn = sortItem.sort_column;
            queryParams.sortDir = sortItem.sort_direction;
        }
        doListItems();
    }

    function onPaginate(pageNumber: number) {
        queryParams.pageNumber = pageNumber;
        doListItems();
    }
</script>

<template>
    <section class="container-full-app bg-grey-50">
        <div class="page-space-30"></div>

        <!--*** Widget *** -->
        <section class="s20">
            <div class="widget shadow-100 pb-2"><!-- widget shadow-100 fill -->
                <!--Card-->
                <div>
                    <div class="container-full-2 my-5">
                        <div class="s7">
                            <h4 class="font-semibold mt-4">
                                Commandes
                                <span class="text-sm text-grey-500">({{ pagination.total }})</span>
                            </h4>
                        </div>
                        <div class="s13 flex justify-content-right">
                            <button class="btn normal ml-4 justify-content-center btn-filtre">
                                <img src="/icons/icon-add.svg" class="btn-icon" alt="" />
                                <span>Créer</span>
                            </button>
                            <div class="form-field-floating default sm s4_mobile ml-3">
                                <select class="form-input-select" id="filters_sort" v-model="queryParams.sortOption" @change="onFiltersChanged">
                                    <option :value="option.value" v-for="(option, index) in SORT_OPTIONS" :key="'filter_sort_' + index">{{ option.label }}</option>
                                </select>
                            </div>
                            <button
                                class="btn normal white2 s3_mobile ml-4 justify-content-center btn-filtre btn-filter">
                                <img src="/icons/icon-filter.svg" class="btn-icon" alt="" />
                                <span>Options</span>
                            </button>
                        </div>
                        <div class="clear-both"></div>
                        <!--Options-->
                        <div class="filter-set-area" style="display: block;">
                            <div class="grid-filter6 mt-4 mb-0">
                                <div class="form-field-floating reactive sm">
                                    <input type="text" class="form-input" id="filters_search" v-model="queryParams.search" placeholder="" @keyup.enter="onFiltersChanged">
                                    <label for="filters_search">Recherche</label>
                                </div>
                                <div class="form-field-floating reactive sm">
                                    <input type="date" class="form-input" id="filters_borne_inf" v-model="queryParams.borneInf" placeholder="" @change="onFiltersChanged">
                                    <label for="filters_borne_inf">Date de création >=</label>
                                </div>
                                <div class="form-field-floating reactive sm">
                                    <input type="date" class="form-input" id="filters_borne_sup" v-model="queryParams.borneSup" placeholder="" @change="onFiltersChanged">
                                    <label for="filters_borne_sup">Date de création &lt;=</label>
                                </div>
                                <div class="form-field-floating sm">
                                    <select class="form-input-select" id="filters_user" v-model="queryParams.user_id" @change="onFiltersChanged">
                                        <option value="">Tous les user</option>
                                        <option :value="option.id" v-for="(option, index) in startupInfos.users" :key="'filter_user_' + index">{{ option.nom }}</option>
                                    </select>
                                    <label for="filters_user">Utilisateur <b class="color-danger">*</b></label>
                                </div>
                            </div>
                        </div>
                        <!---->
                    </div>
                    <hr class="s20 my-0 hr2 opacity-1" />
                    <table class="table separator skin2 selection">
                        <!-- selection skin1 skin2 (bg1 bg2 bg3)  (skin3 rounded)  -->
                        <thead>
                            <tr> 
                                <th>
                                    <label class="btn-circle" for="check-list1">
                                        <input type="checkbox" id="check_all" :checked="areAllItemsSelected" @change="handleCheckAll" />
                                    </label>
                                </th>
                                <th></th>
                                <th><img src="/icons/icon-filter-down.svg" class="size-xxs mt--1" alt="" /> Date</th>
                                <th><img src="/icons/icon-filter-none.svg" class="size-xxs mt--1" alt="" /> Utilisateur</th>
                                <th><img src="/icons/icon-filter-none.svg" class="size-xxs mt--1" alt="" /> Num commande</th>
                                <th><img src="/icons/icon-filter-none.svg" class="size-xxs mt--1" alt="" /> Status</th>
                                <th><img src="/icons/icon-filter-none.svg" class="size-xxs mt--1" alt="" /> Montant</th>
                                <th><img src="/icons/icon-filter-none.svg" class="size-xxs mt--1" alt="" /> Mode Paiement</th>
                                <th class="flex-right">
                                    <button class="btn-ultra white2 tips display-none"><!-- loading sm tips color2 light white white2 success info warning danger-->
                                        <div class="btn-show">
                                            <img src="/icons/icon-destroy.svg" class="btn-icon icon-neutral" alt="" />
                                            <span>Détruire</span>
                                        </div>
                                        <div class="btn-loading">
                                            <div class="loader-dots"></div>
                                        </div>
                                    </button>
                                    <button class="btn-ultra white2 tips"><!--loading-->
                                        <div class="btn-show">
                                            <img src="/icons/icon-delete.svg" class="btn-icon" alt="" />
                                            <span>Supprimer</span>
                                        </div>
                                        <div class="btn-loading">
                                            <div class="loader-dots"></div>
                                        </div>
                                    </button>
                                    <button class="btn-ultra white2 tips"><!--loading-->
                                        <div class="btn-show">
                                            <img src="/icons/icon-restaurer.svg" class="btn-icon icon-neutral" alt="" />
                                            <span>Restaurer</span>
                                        </div>
                                        <div class="btn-loading">
                                            <div class="loader-dots"></div>
                                        </div>
                                    </button>
                                    <button class="btn-ultra color2 tips">
                                        <div class="btn-show">
                                            <img src="/icons/icon-actif.svg" class="btn-icon" alt="" />
                                            <span>Actif</span>
                                        </div>
                                        <div class="btn-loading">
                                            <div class="loader-dots"></div>
                                        </div>
                                    </button>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <template v-if="itemsList.length == 0">
                                <tr>
                                    <td colspan="5">
                                        <div class="text-center text-grey-700">Aucun élément trouvé</div>
                                    </td>
                                </tr>
                            </template>
                            <tr class="text-grey-700 over-effect" v-for="(item, index) in itemsList" :key="'item_' + index"><!-- over-effect active-->
                                <td class="selection-item">
                                    <label class="btn-circle" :for="'item_check_' + index">
                                        <input type="checkbox" name="selectedItems[]" :id="'item_check_' + index" :value="item.id" v-model="selectedItems" />
                                    </label>
                                </td>
                                <td class="flex">
                                    <div class="square-xl rounded-15" style="background-color: rgb(var(--color-warning-rgb), 0.2);">
                                        <img src="/assets/users/300w/user2.jpg" width="100%" />
                                    </div>
                                    <div class="square-xl rounded-15 ml-3 px-3 py-3" style="background-color: rgb(var(--color-info-rgb), 0.2);">
                                        <img src="/icons/icon-menu-carte.svg" width="100%" />
                                    </div>
                                </td>
                                <td>{{ formatDate(item.created_at) }}</td>
                                <td>
                                    <h5 class="mb-1 font-semibold">{{ item.user.nom + ' ' + item.user.prenom }}</h5> 
                                    <span class="text-sm text-grey-500">{{ item.user.fonction }}</span>
                                </td>
                                <td><h6>{{ item.num_commande }}</h6></td>
                                <td><h6>{{ item.statut }}</h6></td>
                                <td><h6>{{ formatAmount(item.total) }}</h6></td>
                                <td><h6>{{ item.mode_paiement }}</h6></td>
                                <td class="flex-right">
                                    <router-link :to="'/app/market/market-commandes/' + item.id">
                                        <button class="btn white2 collapse-open-table">
                                            <img src="/icons/icon-list.svg" class="btn-icon" alt="" />
                                            <span>Plus</span>
                                        </button>
                                    </router-link>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div class="clear-both h-30"></div>

                <div style="display: flex; flex-direction: row; justify-content: center;">
                    <custom-pagination v-model="queryParams.pageNumber" :total="pagination.total" :per-page="pagination.per_page" :last_page="pagination.last_page" @update:modelValue="onPaginate" />
                </div>
            </div>
        </section>

        <div class="clear-both h-30"></div>
        <confirmation-modal ref="confirmationModalRef"></confirmation-modal>
    </section>
</template>
